import Animation from './animation'
import points from '../points'

/*
 * Turns a rangeId into a real point based on the prevPoints
 */
const lookupPoint = (rangeId, prevPoints) => {
  return prevPoints.find(point => point.rangeId === rangeId)
}

/*
 * This animation is used points ticks are added and/or removed
 * from the x-axis. e.g. when switching calendar periods.
 */
class SlidingAnimation extends Animation {

  /*
   * Determines a centering x position based on the scale and the
   * bandwidth of the scale
   */
  static center (scale, bandwidth) {
    return d => scale(d) + bandwidth / 2
  }

  match (serie) {
    if (!this.previous.scalers.x || this.seriesChanged) {
      // We don't have previous data or series changed, so we're not sliding
      return false
    }
    let domain0 = this.previous.scalers.x.groupScale.domain()
      , domain1 = this.scalers.x.groupScale.domain()
      , domain0a = domain0[0]
      , domain0b = domain0[domain0.length - 1]
      , domain1a = domain1[0]
      , domain1b = domain1[domain1.length - 1]

    if (typeof domain0a === 'object') {
      // Assuming all items of the domain are objects (i.e. initiative chart times)
      domain0a = domain0a.toString()
      domain0b = domain0b.toString()
      domain1a = domain1a.toString()
      domain1b = domain1b.toString()
    }

    return domain0a != domain1a || // Start ticks don't match
           domain0b != domain1b // End ticks don't match
  }

  /*
   * Used when ticks on the left side of the chart changed
   */
  _adjustLeft (scale0, scale1, step0, step1, points, prevPoints) {
    let domain0 = scale0.domain()
      , domain1 = scale1.domain()
      , idx = domain1.indexOf(domain0[0])

    if (idx !== -1) {
      // Ticks were added to the left
      // Extend the range on the left side
      scale0.range([scale0.range()[0] - step0 * idx, scale0.range()[1]])

      // Add temp placeholders during the animation for missing points. See #509
      domain1.forEach(rangeId => {
        if (!points.find(p => p.rangeId === rangeId)) {
          points.unshift({rangeId})
        }
      })

    } else {
      // The new domain doesn't have the first tick in the old domain.
      // Find which ticks we lost
      idx = domain0.indexOf(domain1[0])

      // extend the range of the ending scale
      scale1.range([scale1.range()[0] - step1 * idx, scale1.range()[1]])

      // Add all the ticks we lost to our points
      domain0.slice(0, idx).reverse().forEach(rangeId => {
        points.unshift(lookupPoint(rangeId, prevPoints) || {rangeId}) // Add placeholder if not found
      })
    }
  }

  /*
   * Used when ticks on the right side changed
   */
  _adjustRight (scale0, scale1, step0, step1, points, prevPoints) {
    let domain0 = scale0.domain()
      , domain1 = scale1.domain()
      , idx = domain1.indexOf(domain0[domain0.length - 1])

    if (idx !== -1) {
      // New ticks on the right
      // Extend the range
      scale0.range([scale0.range()[0], scale0.range()[1] + step0 * (domain1.length - ++idx)])

      // Add placeholder ticks in the series for each item in the new domain1
      // if they don't exist. See #509
      domain1.forEach(rangeId => {
        if (!points.find(p => p.rangeId === rangeId)) {
          points.push({rangeId})
        }
      })
    } else {
      // We lost ticks on the right
      // Determine what ticks we lost, and add them to the new scale so they slide out
      idx = domain0.indexOf(domain1[domain1.length - 1]) + 1

      // Adjust the range of the ending scale
      scale1.range([scale1.range()[0], scale1.range()[1] + step1 * (domain0.length - idx)])

      // Add the lost ticks to the points
      domain0.slice(idx).forEach(rangeId => {
        points.push(lookupPoint(rangeId, prevPoints) || {rangeId}) // Add placeholder
      })
    }
  }

  _transitionPaths (points, selection, merged, scale0, scale1, bandwidth0, bandwidth1) {
    let centerScale0 = SlidingAnimation.center(scale0, bandwidth0)
      , centerScale1 = SlidingAnimation.center(scale1, bandwidth1)

    this.pathAdjuster.transitionPaths(points, selection, merged, centerScale0, centerScale1)
  }

  animate (serie, entering, selection, merged, exiting) {
    // Sliding this serie

    let points = [...serie.points] // Create a clone since we're manipulating the points for this transition
      , prevPoints = merged.selection().node().parentNode.__previousPoints

    // Determining the starting transition variables
    let scale0 = this.previous.scalers.x.groupScale.copy()
      , bandwidth0 = scale0.bandwidth()
      , step0 = scale0.step()
      , domain0 = scale0.domain()

    // Determine the ending transition variables
    let scale1 = this.scalers.x.groupScale.copy()
      , domain1 = scale1.domain()
      , bandwidth1 = scale1.bandwidth()
      , step1 = scale1.step()

    if (!domain0.filter(tick => domain1.includes(tick)).length) {
      // All ticks were replaced. We're matching the x-axis and will slide all current ticks to the right
      // and incoming ticks come in from the left. As a future enhancement, we might want to determine if we should
      // slide left or slide right.
      scale0.range([scale0.range()[0] - step0 * domain1.length, scale0.range()[1]])
      scale1.range([scale1.range()[0], scale1.range()[1] + step1 * domain0.length])
      domain0.forEach(rangeId => {
        points.push(lookupPoint(rangeId, prevPoints))
      })
    } else {
      if (domain0[0] !== domain1[0]) {
        this._adjustLeft(scale0, scale1, step0, step1, points, prevPoints)
      }

      if (domain0[domain0.length -1] !== domain1[domain1.length -1]) {
        this._adjustRight(scale0, scale1, step0, step1, points, prevPoints)
      }
    }

    // Update both scale's domain to the points we're going to show
    let _domain = points.map(p => p.rangeId)
    scale0.domain(_domain)
    scale1.domain(_domain)

    this.forecast.render(merged, scale0, scale1)

    this._transitionPaths(points, selection, merged, scale0, scale1, bandwidth0, bandwidth1)

    this._points(merged, scale0, scale1, bandwidth0, bandwidth1, points)

    merged.end().then(() => {
      // Now rebind the data so it's properly bound
      this._rebindPaths(selection, serie.points)

      this._points(merged, scale0, scale1, bandwidth0, bandwidth1)
    }).catch(() => { /* Interruptted */ })
  }

  _points (selection, scale0, scale1, bandwidth0, bandwidth1, data) {
    selection.call(points({
      x: SlidingAnimation.center(scale0, bandwidth0),
      y: this.previous.yScale
    }, {
      x: SlidingAnimation.center(scale1, bandwidth1),
      y: this.yScale
    }, this.size, this.chart, this.filled, data))
  }

}

export default SlidingAnimation
